import { createContext, useContext } from 'react';

import { type DynamicWidgetDiagnosticRequest } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_diagnostic';
import { type DynamicWidgetModel } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';

/*
 * Host-provided support handlers behind the error catalog's controls, plus the
 * model the current widget subtree renders. Issue notices read both from
 * context so a control appears only when the host can actually run it — the
 * catalog allows a control, and a handler backs it.
 */

export type DynamicWidgetSupportActions = {
    /**
     * Sends a normal user message with the diagnostic attached. Returns false
     * when the host refused to send (for example an admin trace, which must
     * never carry widget metadata).
     */
    readonly onAskPhoebe?: (
        diagnostic: DynamicWidgetDiagnosticRequest,
    ) => Promise<boolean | void> | boolean | void;
    /** Opens the binding inspector focused on this binding. */
    readonly onShowSource?: (
        widgetId: string,
        bindingId: string | null,
    ) => void;
    /**
     * True only while the internal-admin debug sidebar is open. Widgets
     * register their models with the inspector store only then.
     */
    readonly isInspectionOpen?: boolean;
};

export const DynamicWidgetSupportActionsContext =
    createContext<DynamicWidgetSupportActions | null>(null);

export function useDynamicWidgetSupportActions(): DynamicWidgetSupportActions | null {
    return useContext(DynamicWidgetSupportActionsContext);
}

export const DynamicWidgetModelContext =
    createContext<DynamicWidgetModel | null>(null);

export function useRenderedDynamicWidgetModel(): DynamicWidgetModel | null {
    return useContext(DynamicWidgetModelContext);
}
